import { useEffect, useState } from 'react';
import { Library, Save, X, Edit2, Trash2, Plus } from 'lucide-react';
import { knowledgeBranchesAPI } from '../services/api.js';

// Knowledge branches — the on-demand lookup corpus. Each branch is a KnowledgeTopic:
// a topicId (stable key + sort order, e.g. "5-Careers-SalesRep"), a title (the branch
// name the AI sees in its menu), and the content it pulls when the caller's question
// matches that branch. The AI keeps a SHORT prompt and looks a branch up on demand
// ("one moment while I gather that…") instead of carrying every article in the prompt —
// which is what silently truncated the old flat knowledge block on 346.
//
// Backed by /admin/knowledge-topics (PUT upserts by topicId, DELETE by row id),
// tenant-scoped + admin-gated on the server. Mirrors AccessCodesEditor.

const TOPIC_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,119}$/;

function emptyDraft() {
  return { topicId: '', title: '', content: '', source: '', active: true };
}
function rowToDraft(row) {
  return {
    topicId: row.topicId || '',
    title: row.title || '',
    content: row.content || '',
    source: row.source || '',
    active: row.active ?? true
  };
}
function draftToPayload(d) {
  return {
    topicId: d.topicId.trim(),
    title: d.title.trim(),
    content: d.content.trim(),
    source: d.source.trim() ? d.source.trim() : null,
    active: !!d.active
  };
}
function preview(text) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  return t.length > 90 ? `${t.slice(0, 90)}…` : t;
}

// Shared field block for both create + edit. `lockId` freezes the branch id on edit
// (it is the key — renaming would fork a new branch, so delete + recreate instead).
function BranchFields({ draft, set }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          className="field-input"
          placeholder="Branch id (e.g. 5-Careers-SalesRep)"
          value={draft.topicId}
          onChange={(e) => set({ ...draft, topicId: e.target.value })}
          maxLength={120}
          title="Renaming the id moves this branch (the id is its key + sort order)."
        />
        <input
          className="field-input"
          placeholder="Title (e.g. Sales representative role)"
          value={draft.title}
          onChange={(e) => set({ ...draft, title: e.target.value })}
          maxLength={300}
        />
      </div>
      <textarea
        className="field-input min-h-[160px]"
        placeholder="The knowledge for this branch. The AI reads from here when a caller's question matches — keep it focused on one topic."
        value={draft.content}
        onChange={(e) => set({ ...draft, content: e.target.value })}
        maxLength={50000}
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
        <input
          className="field-input"
          placeholder="Source (optional — where this came from)"
          value={draft.source}
          onChange={(e) => set({ ...draft, source: e.target.value })}
          maxLength={200}
        />
        <label className="flex items-center gap-2 text-sm text-stone-300">
          <input type="checkbox" checked={!!draft.active} onChange={(e) => set({ ...draft, active: e.target.checked })} />
          Active (the AI can use this branch)
        </label>
      </div>
    </div>
  );
}

export default function KnowledgeBranchesEditor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);
  const [creating, setCreating] = useState(null); // draft | null
  const [editing, setEditing] = useState({}); // id -> draft

  const load = async () => {
    setLoading(true);
    try {
      const r = await knowledgeBranchesAPI.list();
      setItems(r?.data || r || []);
    } catch (err) {
      setError(err.message || 'Failed to load knowledge branches.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const validate = (d) => {
    if (!TOPIC_ID_RE.test(d.topicId.trim())) return 'Branch id: letters, digits, dot, dash or underscore (start alphanumeric).';
    if (!d.title.trim()) return 'Title is required.';
    if (!d.content.trim()) return 'Content is required.';
    return null;
  };

  const create = async () => {
    const msg = validate(creating);
    if (msg) { setError(msg); return; }
    if (items.some((r) => r.topicId === creating.topicId.trim())) {
      setError('A branch with that id already exists — edit it instead.'); return;
    }
    try {
      await knowledgeBranchesAPI.upsert(draftToPayload(creating));
      setCreating(null); setSavedAt(new Date()); setError('');
      await load();
    } catch (err) { setError(err.message || 'Create failed.'); }
  };

  const startEdit = (row) => setEditing((s) => ({ ...s, [row.id]: rowToDraft(row) }));
  const cancelEdit = (id) => setEditing((s) => { const n = { ...s }; delete n[id]; return n; });

  const saveEdit = async (id) => {
    const draft = editing[id];
    const msg = validate(draft);
    if (msg) { setError(msg); return; }
    const orig = items.find((r) => r.id === id);
    const newId = draft.topicId.trim();
    const renamed = orig && orig.topicId !== newId;
    if (renamed && items.some((r) => r.id !== id && r.topicId === newId)) {
      setError('Another branch already uses that id.'); return;
    }
    try {
      // Renaming = upsert under the new id, then drop the old row (topicId is the key).
      await knowledgeBranchesAPI.upsert(draftToPayload(draft));
      if (renamed) await knowledgeBranchesAPI.remove(id);
      cancelEdit(id); setSavedAt(new Date()); setError('');
      await load();
    } catch (err) { setError(err.message || 'Save failed.'); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete the "${row.title}" branch? The AI will no longer have this knowledge.`)) return;
    try {
      await knowledgeBranchesAPI.remove(row.id);
      setItems((list) => list.filter((p) => p.id !== row.id));
      setSavedAt(new Date());
    } catch (err) { setError(err.message || 'Delete failed.'); }
  };

  return (
    <section className="card">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Library size={18} className="text-alfred-gold" />
          <h2 className="text-xl text-stone-100">Knowledge branches</h2>
        </div>
        {savedAt && <span className="text-xs text-stone-500">Saved {savedAt.toLocaleTimeString()}</span>}
      </header>

      <p className="text-sm text-stone-400 mb-4">
        The knowledge the assistant looks up mid-call instead of carrying in its prompt.
        Each branch is one focused topic; when a caller asks about it, the AI says
        “one moment…”, pulls that branch, and answers from it. The branch id sets the
        order — a numeric prefix (e.g. <span className="font-mono text-stone-300">1-Company</span>)
        keeps them tidy.
      </p>

      {error && (
        <p className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>
      )}

      {!creating && (
        <button type="button" className="btn-ghost mb-4" onClick={() => { setCreating(emptyDraft()); setError(''); }}>
          <Plus size={14} /> Add branch
        </button>
      )}

      {creating && (
        <div className="mb-4 space-y-3 rounded-lg border border-alfred-gold/30 p-4">
          <BranchFields draft={creating} set={setCreating} />
          <div className="flex gap-2">
            <button type="button" className="btn-primary" onClick={create}>
              <Save size={14} /> Save
            </button>
            <button type="button" className="btn-ghost" onClick={() => { setCreating(null); setError(''); }}>
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-stone-500">No knowledge branches yet.</p>
      ) : (
        <ul className="divide-y divide-stone-900">
          {items.map((row) => {
            const isEditing = !!editing[row.id];
            return (
              <li key={row.id} className="py-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-stone-100 font-medium">
                      <span className="font-mono text-alfred-gold">{row.topicId}</span>
                      <span className="ml-2">{row.title}</span>
                      {!row.active && <span className="ml-2 text-xs text-stone-500 italic">— inactive</span>}
                    </div>
                    <div className="text-xs text-stone-500">{preview(row.content)}</div>
                  </div>
                  {!isEditing && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button type="button" className="btn-ghost" onClick={() => startEdit(row)}>
                        <Edit2 size={14} /> Edit
                      </button>
                      <button type="button" className="btn-ghost" onClick={() => remove(row)}>
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  )}
                </div>

                {isEditing && (
                  <div className="mt-4 space-y-3 rounded-lg border border-alfred-gold/30 p-4">
                    <BranchFields draft={editing[row.id]} set={(d) => setEditing((s) => ({ ...s, [row.id]: d }))} />
                    <div className="flex gap-2">
                      <button type="button" className="btn-primary" onClick={() => saveEdit(row.id)}>
                        <Save size={14} /> Save
                      </button>
                      <button type="button" className="btn-ghost" onClick={() => cancelEdit(row.id)}>
                        <X size={14} /> Cancel
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
